import { SaveOutlined } from '@ant-design/icons';
import { Checkbox, Col, Form, Input, message, Modal, Row, Select, Tag } from 'antd';
import React, { useState } from 'react';
import ZTable, { ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import CreateBtn from '@/components/app/CreateBtn';
import BooleanTag from '@/components/app/BooleanTag';
import { TariffParameterDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import ColorPicker from '@/components/FormItems/ColorPicker';

const columns: IColumnType<TariffParameterDto>[] = [
  {
    title: 'base.Name',
    dataIndex: 'name',
  },

  {
    title: 'Seat Color',
    dataIndex: 'seatColor',
    render: v => <span style={{ color: v }}>{v}</span>,
  },
  {
    title: 'base.IsStandard',
    dataIndex: 'isStandard',
    render: v => <BooleanTag value={v} />,
  },
];

const BaseTicketClassTab = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const createEntity = values => {
    setLoading(true);
    apiSdk.TicketClassService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.TicketClassService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/fleets/ticket-classes/get-ticket-classes"
        bordered
        order_by="createdOn"
        order_by_asc={true}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={true}
        delete_url="/fleets/ticket-classes/delete"
        searchBtnAlignLeft
        extraBtns={
          <CreateBtn
            onClick={() => {
              setIsNewForm(true);
              setFormVisible(true);
              form.resetFields();
            }}
          />
        }
      ></ZTable>
      {formVisible ? (
        <Modal
          open={formVisible}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={isNewForm ? 'New ticket class' : 'Update ticket class'}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateEntity(values);
                } else {
                  createEntity(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item label={t('base.Name')} name="name" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
                <Form.Item label={'Seat Color'} name="seatColor" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <ColorPicker />
                </Form.Item>
                <Form.Item label={t('base.IsStandard')} name="isStandard" valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
    </div>
  );
};
export default BaseTicketClassTab;
